import { install as _install, event as _event } from 'vue-gtag'
import { defaults } from 'lodash'

/**
 * This plugin wraps vue-gtag and customizes it for use in the Vue application. Most importantly, it lets us pass
 * along additional state from the VueX store in the form of user and customer context.
 */

/**
 * @var _store the VueX store
 */
let _store

/**
 * Produces an object with the common metadata which should be sent on ever
 * hit (pageview, event, etc...)
 *
 * @return object
 */
function commonHitMetadata () {
  const user = _store.getters['userModule/getUser']
  const customerContext = _store.getters['userModule/customerContext']

  // Map known user roles to a comma separated string of role codes
  let userRoles
  if (typeof user.roles === 'object') {
    userRoles = user.roles.map(role => {
      return role.code
    }).join(',')
  }

  return {
    user_id: user.id,
    customer: customerContext.name,
    user_roles: userRoles
  }
}

/**
 * Wrap VueGtag event method to use metadata format defined by commonHitMetadata.
 * @inheritDoc
 * @param action
 * @param meta
 */
function event (action, meta) {
  if (meta.event_category == null || !eventCategories().includes(meta.event_category)) {
    throw new Error(`Event category: ${meta.event_category} is invalid. Please use one of: ${eventCategories().join(', ')}`)
  }
  const eventMeta = defaults({}, meta, commonHitMetadata())
  // Invoke original method (as imported directly from vue-gtag module)
  _event(action, eventMeta)
}

/**
 * Provides the template of data to send with each pageview registered by the
 * router see more at
 * https://matteo-gabriele.gitbook.io/vue-gtag/auto-tracking#page-tracker-template
 *
 * @param to object the route to which we have transitioned
 * @return object
 */
function pageTrackerTemplate (to) {
  const template = {
    page_path: to.path,
    page_location: window.location.href
  }

  return defaults(template, commonHitMetadata())
}

/**
 * Returns an array of valid event categories. This should be used to ensure
 * consistent use of event categories across the application. These map
 * closely, but not exactly, to models; for example there is not model for
 * CustomerContext, RouteDashboard, or TimeDisplay however these are
 * categories of events we want to track independently
 *
 * @return array
 */
function eventCategories () {
  return [
    'ApiAccessLog',
    'AuditLog',
    'Banner',
    'Customer',
    'CustomerContext',
    'CustomerSetting',
    'EnergyPriceSet',
    'HighReeferActivityVehicle',
    'Location',
    'Notification',
    'NotificationRule',
    'Product',
    'ProductStartTemp',
    'Report',
    'Route',
    'RouteAssessment',
    'RouteDashboard',
    'RoutePlan',
    'RouteProduct',
    'SustainabilityRoute',
    'TimeDisplay',
    'UnregisteredLocation',
    'User',
    'UserSetting',
    'Vehicle',
    'Invoice',
    'RoutePlanExclude',
    'RoutePlanRule',
    'RoutePlanUpload',
    'RoutePlanFileUpload'
  ]
}

/**
 * Install the module.
 * @param Vue
 * @param options
 * @param router
 * @param store
 */
function install (Vue, options, router, store) {
  _store = store
  // invoke vue-gtag installer
  _install(Vue, options, router)
  // Override $gtag.event
  Vue.$gtag.event = event
}

const VerofreshGtag = {
  install: install,
  pageTrackerTemplate: pageTrackerTemplate
}

export default VerofreshGtag
